import moment from 'moment';
import { MEASUREMENT } from 'src/constants/common';

export const CP_ORDER_TABLE_ALIAS = 'cp_orders';

export const MAX_ORDER_VIEW_COUNT = 11;

export const SORTABLE_INITIAL_STATE = {
    isOpen: false,
    data: [],
};

export const BULK_SELECTION_INITIAL_STATE = {
    isOpen: false,
    data: [] as number[],
};

export const DEFAULT_ORDER_TAB_VALUES = ['all', 'booked', 'dispatched', 'in-transit', 'delivered', 'completed'];

export const CP_ORDER_DEFAULT_COLUMNS = [
    { id: 'orderId', name: 'Order #', visible: true, displayOrder: 1 },
    { id: 'salesRep', name: 'Created By', visible: true, displayOrder: 2 },
    { id: 'orderStatus', name: 'Status', visible: true, displayOrder: 3 },
    { id: 'orderType', name: 'Type', visible: true, displayOrder: 4 },
    { id: 'shipperFullAddress', name: 'Origin', visible: true, displayOrder: 5 },
    { id: 'consigneeFullAddress', name: 'Destination', visible: true, displayOrder: 6 },
    { id: 'createdAt', name: 'Created', visible: true, displayOrder: 7 },
    { id: 'handlingUnits', name: 'Handling Unit', visible: true, displayOrder: 8 },
    { id: 'totalWeight', name: 'Total Weight', visible: true, displayOrder: 9 },
    { id: 'totalCost', name: 'Cost', visible: true, displayOrder: 10 },
    { id: 'updatedAt', name: 'Last Modified', visible: true, displayOrder: 11 },
    { id: 'pickupDate', name: 'Pickup Date', visible: false, displayOrder: 12 },
    { id: 'deliveryDate', name: 'Delivery Date', visible: false, displayOrder: 13 },
    { id: 'carrier', name: 'Carrier', visible: false, displayOrder: 14 },
    { id: 'bolNumber', name: 'BOL #', visible: false, displayOrder: 15 },
    { id: 'refNumber', name: 'Reference #', visible: false, displayOrder: 16 },
    { id: 'linearFootage', name: 'Linear Feet', visible: false, displayOrder: 17 },
    { id: 'equipmentType', name: 'Equipment', visible: false, displayOrder: 18 },
];

export const COLUMN_CLASSNAME_MAP: Record<string, string> = {
    // Fluid width, 1:1 with wal-frontend-v2's Orders listing (NewOrderRow `max-w-px min-w-[208px]`).
    orderId: 'max-w-px min-w-[200px]',
    salesRep: 'min-w-[150px] max-w-[150px] w-[150px]',
    orderStatus: 'min-w-[125px] max-w-[125px] w-[125px]',
    orderType: 'min-w-[100px] max-w-[100px] w-[100px]',
    shipperFullAddress: 'text-xs xxl:max-w-[170px] xxl:w-[170px] xxl:min-w-[170px] w-40 max-w-40 min-w-40',
    consigneeFullAddress: 'text-xs xxl:max-w-[170px] xxl:w-[170px] xxl:min-w-[170px] w-40 max-w-40 min-w-40',
    createdAt: 'min-w-[165px] w-[165px] max-w-[165px]',
    handlingUnits: 'min-w-[155px] w-[155px] max-w-[155px]',
    totalWeight: 'min-w-[140px] w-[140px] max-w-[140px]',
    totalCost: 'min-w-[145px] w-[145px] max-w-[145px]',
    updatedAt: 'min-w-[165px] w-[165px] max-w-[165px]',
    pickupDate: 'min-w-[155px] w-[155px] max-w-[155px]',
    deliveryDate: 'min-w-[155px] w-[155px] max-w-[155px]',
    carrier: 'min-w-[190px] max-w-[190px]',
    bolNumber: 'min-w-[145px] w-[145px] max-w-[145px]',
    refNumber: 'min-w-[145px] w-[145px] max-w-[145px]',
    linearFootage: 'min-w-[130px] w-[130px] max-w-[130px]',
    equipmentType: 'min-w-[145px] w-[145px] max-w-[145px]',
    action: 'min-w-[38px] max-w-[38px] w-[38px] table-cell-with-tooltip',
};

export const COLUMN_TRANSLATION_MAP: Record<string, string> = {
    orderStatus: 'Common.Status',
    orderType: 'Common.Type',
    shipperFullAddress: 'Common.Origin',
    consigneeFullAddress: 'Common.Destination',
    createdAt: 'Common.Order_Created',
    handlingUnits: 'Common.Handling_Unit',
    totalWeight: 'Common.Total_Weight',
    totalCost: 'Common.Cost',
    updatedAt: 'Common.Order_Updated',
};

import { IMapAddress, AppointmentProps, IDimensionProps } from './order.interface';

export const initShipperAddress: IMapAddress = {
    fullAddress: '',
    address1: '',
    city: '',
    state: '',
    country: '',
    postal: '',
    latitude: '',
    longitude: '',
    stateCode: '',
    countryCode: '',
    streetNumber: '',
};

export const initShipper = {
    id: null,
    companyName: '',
    contactId: null,
    contactName: '',
    phoneNumber: '',
    email: '',
    shipperAddress: initShipperAddress,
};

export const initConsignee = {
    id: null,
    contactId: null,
    companyName: '',
    contactName: '',
    phoneNumber: '',
    email: '',
    consigneeAddress: initShipperAddress,
};

export const classOptions = [
    {
        label: 'LTL',
        value: 'ltl',
    },
    {
        label: 'FTL',
        value: 'ftl',
    },
];

export const initAction = {
    mode: null,
    shipper: false,
    consignee: false,
    type: null,
    status: null,
    isEditMode: false,
    url: null,
    carrierId: null,
};

export const dimensionObj: IDimensionProps = {
    handlingUnit: undefined,
    handlingUnitNo: undefined,
    length: undefined,
    width: undefined,
    height: undefined,
    weight: undefined,
    totalWeight: undefined,
    freightClass: undefined,
    refNumber: undefined,
    isStack: true,
    itemClassForDayross: 0,
    description: undefined,
    unitPrice: '',
    unitPriceCurrency: '',
};

export const defualtOrderValue = {
    shipper: {
        companyName: '',
        contactName: '',
        phoneNumber: '',
        email: '',
        shipperAddress: initShipperAddress,
    },
    consignee: {
        companyName: '',
        contactName: '',
        phoneNumber: '',
        email: '',
        consigneeAddress: initShipperAddress,
    },
    orderType: 'ltl',
    equipmentTypeFullName: 'Dry Van',
    equipmentType: 'V',
    poNumber: '',
    refNumber: '',
    linearFootage: '',
    totalWeight: '',
    declaredValue: '',
    specialNotes: '',
    declaredValueUnit: 'CAD',
    order_dimensions: [dimensionObj],
    additionalServices: null,
    carrierPrice: [],
    salesPrice: [],
    weightMeasurement: MEASUREMENT.WEIGHT1.value,
    lengthMeasurement: MEASUREMENT.LENGTH1.value,
    expectedPickupTime: moment().local().toDate(),
    expectedEstimatedDeliveryTime: moment().local().toDate(),
    shipperAppointmentDate: undefined,
    shipperAppointmentTime: undefined,
    carrierPickupAppointmentDate: undefined,
    carrierPickuAppointmentTime: undefined,
    deliveryAppointmentDate: undefined,
    deliveryAppointmentTime: undefined,
    carrierDeliveryAppointmentDate: undefined,
    carrierDeliveryAppointmentTime: undefined,
};

export const pickupAppointments: AppointmentProps[] = [
    {
        title: 'Shipper Pickup Appointment',
        description: 'Add a Shipper Pickup Appointment',
        mode: 'shipper-appointment',
        field: 'shipperAppointmentDatetime',
        status: 'shipperAppointmentStatus',
        dateFieldName: 'shipperAppointmentDate',
        timeFieldName: 'shipperAppointmentTime',
    },
    {
        title: 'Carrier Pickup Appointment',
        description: 'Add a Carrier Pickup Appointment',
        mode: 'carrier-pickup-appointment',
        field: 'carrierPickupAppointmentDatetime',
        status: 'carrierPickupAppointmentStatus',
        dateFieldName: 'carrierPickupAppointmentDate',
        timeFieldName: 'carrierPickuAppointmentTime',
    },
];

export const deliveryAppointments: AppointmentProps[] = [
    {
        title: 'Consignee Delivery Appointment',
        description: 'Add a Consignee Delivery Appointment',
        mode: 'delivery-appointment',
        field: 'deliveryAppointmentDatetime',
        status: 'deliveryAppointmentStatus',
        dateFieldName: 'deliveryAppointmentDate',
        timeFieldName: 'deliveryAppointmentTime',
    },
    {
        title: 'Carrier Delivery Appointment',
        description: 'Add a Carrier Delivery Appointment',
        mode: 'carrier-delivery-appointment',
        field: 'carrierDeliveryAppointmentDatetime',
        status: 'carrierDeliveryAppointmentStatus',
        dateFieldName: 'carrierDeliveryAppointmentDate',
        timeFieldName: 'carrierDeliveryAppointmentTime',
    },
];

export const fieldMapping: any = {
    'shipper-appointment': {
        datetimeField: 'shipperAppointmentDatetime',
        statusField: 'shipperAppointmentStatus',
        dateFieldName: 'shipperAppointmentDate',
        timeFieldName: 'shipperAppointmentTime',
    },
    'carrier-pickup-appointment': {
        datetimeField: 'carrierPickupAppointmentDatetime',
        statusField: 'carrierPickupAppointmentStatus',
        dateFieldName: 'carrierPickupAppointmentDate',
        timeFieldName: 'carrierPickuAppointmentTime',
    },
    'carrier-delivery-appointment': {
        datetimeField: 'carrierDeliveryAppointmentDatetime',
        statusField: 'carrierDeliveryAppointmentStatus',
        dateFieldName: 'carrierDeliveryAppointmentDate',
        timeFieldName: 'carrierDeliveryAppointmentTime',
    },
    'delivery-appointment': {
        datetimeField: 'deliveryAppointmentDatetime',
        statusField: 'deliveryAppointmentStatus',
        dateFieldName: 'deliveryAppointmentDate',
        timeFieldName: 'deliveryAppointmentTime',
    },
    'delivery-date': {
        datetimeField: 'expectedEstimatedDeliveryTime',
    },
    'pickup-date': {
        datetimeField: 'expectedPickupTime',
    },
};

export const appointmentDetails: any = {
    'carrier-pickup-appointment': {
        title: 'Carrier Pickup Appointment',
        description: 'Assign a carrier to complete your shipment',
        draftTitle: 'RE: Request to Set Carrier Pickup Appointment',
        sendMailTo: 'carrier',
        emailDraftModalTitle: 'Confirm Carrier Pickup Appointment',
        messages: {
            Pending: 'Are you sure you want to mark the pickup appointment with the carrier as pending?',
            Confirmed: 'Are you sure you want to confirm the pickup appointment with the carrier?',
        },
        successModelTitle: 'Carrier Appointment Set!',
        successModelDescription: 'Your appointment was successfully set.Would you like to draft an email to notify your carrier?',

        successModelEditTitle: 'Carrier Appointment Edited!',
        successModelEditDescription: 'Your appointment has been edited.Would you like to draft an email to notify your carrier?',
        emailContent: 'carrier appointment for pickup',
    },
    'carrier-delivery-appointment': {
        title: 'Carrier Delivery Appointment',
        description: 'Assign a carrier to complete your shipment',
        draftTitle: 'RE: Request to Set Carrier Delivery Appointment',
        sendMailTo: 'carrier',
        emailDraftModalTitle: 'Confirm Carrier Delivery Appointment',
        messages: {
            Pending: 'Are you sure you want to mark the delivery appointment with the carrier as pending?',
            Confirmed: 'Are you sure you want to confirm the delivery appointment with the carrier?',
        },

        successModelTitle: 'Carrier Appointment Set!',
        successModelDescription: 'Your appointment was successfully set.Would you like to draft an email to notify your carrier?',

        successModelEditTitle: 'Carrier Appointment Edited!',
        successModelEditDescription: 'Your appointment has been edited.Would you like to draft an email to notify your carrier?',
        emailContent: 'carrier appointment for delivery',
    },
    'shipper-appointment': {
        title: 'Shipper Appointment',
        description: 'Assign a shipper to complete your shipment',
        draftTitle: 'RE: Request to Set Shipper Pickup Appointment',
        sendMailTo: 'shipper',
        emailDraftModalTitle: 'Confirm Shipper Pickup Appointment',
        messages: {
            Pending: 'Are you sure you want to mark the pickup appointment with the shipper as pending?',
            Confirmed: 'Are you sure you want to confirm the pickup appointment with the shipper?',
        },

        successModelTitle: 'Shipper Appointment Set!',
        successModelDescription: 'Your appointment was successfully set.Would you like to draft an email to notify your shipper?',

        successModelEditTitle: 'Shipper Appointment Edited!',
        successModelEditDescription: 'Your appointment has been edited.Would you like to draft an email to notify your shipper?',
        emailContent: 'shipper appointment for pick-up',
    },
    'delivery-appointment': {
        title: 'Consignee Appointment',
        description: 'Assign a consignee to complete your shipment',
        draftTitle: 'RE: Request to Set Delivery Appointment',
        sendMailTo: 'consignee',
        emailDraftModalTitle: 'Confirm Consignee Delivery Appointment',
        messages: {
            Pending: 'Are you sure you want to mark the delivery appointment with the consignee as pending?',
            Confirmed: 'Are you sure you want to confirm the delivery appointment with the consignee?',
        },

        successModelTitle: 'Consignee Appointment Set!',
        successModelDescription: 'Your appointment was successfully set.Would you like to draft an email to notify your consignee?',

        successModelEditTitle: 'Consignee Appointment Edited!',
        successModelEditDescription: 'Your appointment has been edited.Would you like to draft an email to notify your consignee?',
        emailContent: 'consignee appointment for delivery',
    },
    'pickup-date': {
        title: 'Pickup Date',
        description: 'Assign a pickup date to schedule your shipment',
        draftTitle: '',
        sendMailTo: '',
        messages: {
            Pending: '',
            Confirm: '',
        },
    },
    'delivery-date': {
        title: 'Delivery Date',
        description: 'Assign a delivery date to schedule your shipment',
        draftTitle: '',
        sendMailTo: '',
        messages: {
            Pending: '',
            Confirm: '',
        },
    },
};

export const ORDER_STATUS_TAB: { value: string; name: string }[] = [
    {
        value: 'all',
        name: 'View All',
    },
    {
        value: 'draft',
        name: 'Drafted',
    },
    {
        value: 'booked',
        name: 'Booked',
    },
    {
        value: 'dispatched',
        name: 'Dispatched',
    },
    {
        value: 'in-transit',
        name: 'In-Transit',
    },
    {
        value: 'delivered',
        name: 'Delivered',
    },
    {
        value: 'cancelled',
        name: 'Cancelled',
    },
    {
        value: 'completed',
        name: 'Completed',
    },
];

export const orderStatusOption: { value: string; label: string }[] = [
    {
        value: 'draft',
        label: 'Drafted',
    },
    {
        value: 'booked',
        label: 'Booked',
    },
    {
        value: 'dispatched',
        label: 'Dispatched',
    },
    {
        value: 'in-transit',
        label: 'In-Transit',
    },
    {
        value: 'delivered',
        label: 'Delivered',
    },
    {
        value: 'cancelled',
        label: 'Cancelled',
    },
    {
        value: 'completed',
        label: 'Completed',
    },
];

export const ORDER_TYPE_TAB: { value: string; name: string }[] = [
    {
        value: 'rose-rocket-order',
        name: 'RoseRocket Order',
    },
    {
        value: 'new-order',
        name: 'End To End Order',
    },
];

export const END_TO_END_ORDER_HEAD_CELLS = [
    {
        id: 'orderId',
        name: 'Order #',
        sortable: true,
        visible: true,
        rowClassName: '!pr-2 !pl-[32px] min-w-[12rem] w-[12rem] max-w-[12rem]',
    },
    {
        id: 'orderStatus',
        name: 'Status',
        sortable: true,
        visible: true,
        rowClassName: 'min-w-[125px] max-w-[125px] w-[125px]',
    },
    {
        id: 'orderType',
        name: 'Type',
        sortable: true,
        visible: true,
        rowClassName: 'min-w-[100px] max-w-[100px] w-[100px]',
    },
    {
        id: 'shipperFullAddress',
        name: 'Origin',
        sortable: true,
        visible: true,
        rowClassName: 'text-xs xxl:max-w-[170px] xxl:w-[170px] xxl:min-w-[170px] w-40 max-w-40 min-w-40',
    },
    {
        id: 'consigneeFullAddress',
        name: 'Destination',
        sortable: true,
        visible: true,
        rowClassName: 'text-xs xxl:max-w-[170px] xxl:w-[170px] xxl:min-w-[170px] w-40 max-w-40 min-w-40',
    },
    {
        id: 'createdAt',
        name: 'Created',
        sortable: true,
        visible: true,
        rowClassName: 'min-w-[165px] w-[165px] max-w-[165px]',
    },
    {
        id: 'handlingUnits',
        name: 'Handling Unit',
        sortable: true,
        visible: true,
        rowClassName: 'min-w-[155px] w-[155px] max-w-[155px]',
    },
    {
        id: 'totalWeight',
        name: 'Total Weight',
        sortable: true,
        visible: true,
        rowClassName: 'min-w-[140px] w-[140px] max-w-[140px]',
    },
    {
        id: 'totalCost',
        name: 'Cost',
        sortable: true,
        visible: true,
        rowClassName: 'min-w-[135px] w-[135px] max-w-[135px]',
    },
];

export const INIT_ROSE_ROCKET_ORDER_PARAMS: any = {
    search: '',
    sortType: 'desc',
    sortField: 'orderCreateTime',
    page: 1,
    limit: 25,
    team: 'all',
    member: 'all',
};

export const INIT_E2E_ORDER_PARAMS: any = {
    search: '',
    sortType: 'desc',
    sortField: 'createdAt',
    page: 1,
    limit: 50,
    status: 'all',
    classification: null,
    member: 'all',
    startDate: null,
    endDate: null,
    shipper: null,
    consignee: null,
};

// Order Status enum values for API
export const ORDER_STATUS = {
    ALL: 'all',
    BOOKED: 'booked',
    DELIVERED: 'delivered',
    DISPATCHED: 'dispatched',
    IN_TRANSIT: 'in-transit',
    COMPLETED: 'completed',
};

// Status options for filter dropdown
export const ORDER_STATUS_OPTIONS = [
    {
        label: 'All',
        value: ORDER_STATUS.ALL,
    },
    {
        label: 'Booked',
        value: ORDER_STATUS.BOOKED,
    },
    {
        label: 'Delivered',
        value: ORDER_STATUS.DELIVERED,
    },
    {
        label: 'Dispatched',
        value: ORDER_STATUS.DISPATCHED,
    },
    {
        label: 'In-Transit',
        value: ORDER_STATUS.IN_TRANSIT,
    },
    {
        label: 'Completed',
        value: ORDER_STATUS.COMPLETED,
    },
];

// Classification options for filter dropdown
export const ORDER_CLASSIFICATION_OPTIONS = [
    {
        label: 'LTL',
        value: 'ltl',
    },
    {
        label: 'FTL',
        value: 'ftl',
    },
];

// Static data for filter dropdowns
export const usersFilterOptions: { value: string; label: string }[] = [
    {
        value: 'user1',
        label: 'John Doe',
    },
    {
        value: 'user2',
        label: 'Jane Smith',
    },
    {
        value: 'user3',
        label: 'Mike Johnson',
    },
    {
        value: 'user4',
        label: 'Sarah Williams',
    },
    {
        value: 'user5',
        label: 'Robert Brown',
    },
];

export const shipperFilterOptions: { value: string; label: string }[] = [
    {
        value: 'shipper1',
        label: 'ABC Logistics Inc.',
    },
    {
        value: 'shipper2',
        label: 'Global Freight Solutions',
    },
    {
        value: 'shipper3',
        label: 'Express Shipping Co.',
    },
    {
        value: 'shipper4',
        label: 'Prime Transport Services',
    },
    {
        value: 'shipper5',
        label: 'Fast Track Logistics',
    },
];

export const consigneeFilterOptions: { value: string; label: string }[] = [
    {
        value: 'consignee1',
        label: 'Warehouse Direct Ltd.',
    },
    {
        value: 'consignee2',
        label: 'Metro Distribution Center',
    },
    {
        value: 'consignee3',
        label: 'Central Supply Hub',
    },
    {
        value: 'consignee4',
        label: 'Regional Depot Services',
    },
    {
        value: 'consignee5',
        label: 'National Retail Group',
    },
];

export const MASTER_ORDER_STEPS = [
    {
        title: 'Add Order Metadata',
        description: 'Please provide the metadata for your order.',
    },
    {
        title: 'Add Shipper',
        description: 'Please provide the shipper details for your order.',
    },
    {
        title: 'Add Consignee',
        description: 'Please provide the consignee details for your order.',
    },
    {
        title: 'Add Pickup and Delivery Dates',
        description: 'Pick dates for when the pickup and delivery should occur.',
    },
    {
        title: 'Add Order Details',
        description: 'Please add the details of your order before proceeding.',
    },
    {
        title: 'Add Carrier & Sale Price',
        description: 'Select the carrier and set the sale price for your order.',
    },
    {
        title: 'Review and Finalize Order',
        description: 'Review every details of your order.',
    },
];

export const STEPS_FOR_LEG_FORM = [
    {
        name: 'Add Shipper',
        step: 1,
    },
    {
        name: 'Add Consignee',
        step: 2,
    },
    {
        name: 'Appointment',
        step: 3,
    },
    {
        name: 'Order Details',
        step: 4,
    },
    {
        name: 'Prices',
        step: 5,
    },
];

export const ordersTimeFilterArray: any = [
    {
        value: 'daily',
        name: 'Today',
    },
    {
        value: 'weekly',
        name: 'This Week',
    },
    {
        value: 'monthly',
        name: 'This Month',
    },
    {
        value: 'yearly',
        name: 'This Year',
    },
];

export const ORDER_DOCUMENT_TYPE: { [key: string]: string } = {
    CUSTOMS_DOCUMENT: 'customs_document',
    BOL: 'BOL',
    RATE_CONFIRMATION: 'rate_confirmation',
    POD: 'POD',
    COMMERCIAL_INVOICE: 'commercial_invoice',
    NEW_DOCUMENTS: 'new_documents',
    CUSTOMER_INVOICE: 'customer_invoice',
    SHIPMENT: 'shipment',
    OTHER: 'other',
};

export const ORDER_DETAILS_TABS: { [key: string]: string } = {
    tracking: 'tracking',
    address_details: 'address_details',
    order_details: 'order_details',
    documents: 'documents',
    latest_updates: 'latest_updates',
};

export type OrderKeysType = 'shipper_details' | 'consignee_details' | 'load_details' | 'order_details' | 'documents' | 'review_finalize';
